/* eslint-disable no-unused-vars, no-param-reassign */
import {Store} from '@reduxjs/toolkit';
import {
  CancelablePromise,
  GetStylesFunction,
  injectNavigate,
  injectStore,
  StateId,
  usePrevious,
  utils,
} from '@wallaby-ui/core';
import {
  ColDef,
  ColumnApi,
  GridApi,
  GridReadyEvent,
  SortModelItem,
} from 'ag-grid-community';
import {AgGridReact} from 'ag-grid-react';
import classNames from 'classnames';
import {
  debounce,
  forEach,
  get,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  map,
} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {NavigateFunction, Params} from 'react-router-dom';
import ResizeObserver from 'resize-observer-polyfill';

import styles from './AgGrid.less';
import {AgGridFilterButtonItems} from './AgGridFilterButtonItems';
import {
  applyDefaultSortModel,
  callGridApiInTimeoutCallback,
  getColumnStates,
  getGridColumnStatePromise,
  getGridHeaderLevelsCount,
  getOverlayNoRowsTemplate,
  getStateFromRows,
  getStateGridColumns,
  hasColumnVisibilityChanged,
  mapSortModelToColumnStateUpdate,
  removeDefaultSortModel,
  setSortModelAndScroll,
  transformRows,
} from '../grid_helpers';
import {
  CurrentSortModelItem,
  MemorizedColumnStates,
  Pagination,
  RowsData,
  SelectedRow,
} from '../grid_types';
import {LIBRARY_NAME} from 'src/js/common/constants';
import {
  GRID_ACTION_FILTER,
  GRID_ACTION_FIRST_RENDER,
  GRID_ACTION_PAGE,
  GRID_ACTION_SCROLL,
  GRID_STATE_LOADING,
  GRID_STATE_STABILIZED,
  GRID_HEADER_HEIGHT,
} from 'src/js/containers/grid/grid_constants';
import {ColumnConfig} from 'src/js/model/column_config';
import {
  TableConfig,
  TABLE_ROW_MAX_SUB_ROWS,
  GRID_OPTIONS_REDRAW_ON_SORT,
} from 'src/js/model/table_config';

const logger = utils.LogUtils.getLogger('AgGrid', LIBRARY_NAME);

const GRID_SCROLLABLE_ELEMENT_SELECTOR = '.ag-body-viewport';
const STATIC_EMPTY_SCROLL = [0, 0];
const GRID_LOCALIZATION_PREFIX = 'agGrid.';

const I18N = defineMessages({
  FILTER: {
    id: 'grid.filterButtons.title',
    defaultMessage: 'Filter',
    description: 'text filter buttons span',
  },
  NO_ROWS: {
    id: 'agGrid.noRowsToShow',
    defaultMessage: 'No Rows To Show',
    description: 'grid text when there are now rows to show',
  },
});

export interface AgGridOwnProps {
  stateId: StateId;
  autoPageSize?: boolean;
  className?: string | Record<string, any> | [];
  columns: ColDef[];
  memorizedColumnStates?: MemorizedColumnStates;
  context?: Record<string, any> | string;
  currentFilter?: Record<string, any>[];
  currentFilterButton?: number;
  currentPagination?: Record<string, any>;
  currentScroll?: number[];
  currentSort?: CurrentSortModelItem[];
  domLayout?: string;
  filterButtonsConfig?: any[];
  gridOptions?: Record<string, any>;
  id?: string;
  isDataTranslated?: boolean;
  isLoading?: boolean;
  isPerformingAction?: boolean;
  isTranslated?: boolean;
  maxHeight?: number;
  // TODO: type functions
  onColumnStatesChange?: (columnStates: MemorizedColumnStates) => void;
  onChangeRouteLoading?: (
    id: string | undefined,
    context: string | Record<string, any> | undefined,
    up: boolean,
    reset: boolean
  ) => void;
  onFilterButtonClick?: (any) => void;
  onFilterChange?: (filterModel: any) => void;
  onPaginationChange?: (pagination: Pagination) => void;
  onUpdatePerformedAction?: (
    componentId,
    context,
    performedActionType,
    remove: boolean,
    reset: boolean
  ) => void;
  onScrollChange?: (scrollPosition: any) => void;
  onSortChange?: (sortModel: any) => void;
  rows?: RowsData;
  scrollToTopOnFilter?: boolean;
  scrollToTopOnSort?: boolean;
  selectedRow?: SelectedRow;
  selectedRowIdDataPath?: string;
  suppressPaginationPanel?: boolean;
  watchLocationParams?: Record<string, any>;
}

type Props = AgGridOwnProps &
  Store &
  NavigateFunction &
  Readonly<Params<string>>;

export const AgGridComponent: React.FC<Props> = (props) => {
  // PROPS
  const {
    autoPageSize = false,
    className = 'ag-theme-balham',
    columns,
    context,
    currentFilter,
    currentFilterButton,
    currentPagination,
    currentScroll,
    currentSort,
    domLayout = 'normal',
    filterButtonsConfig = [],
    gridOptions = {},
    id = '',
    isDataTranslated = true,
    isLoading = false,
    isPerformingAction = false,
    isTranslated = true,
    memorizedColumnStates,
    navigate,
    onColumnStatesChange,
    onChangeRouteLoading,
    onFilterChange,
    onFilterButtonClick,
    onPaginationChange,
    onScrollChange,
    onSortChange,
    onUpdatePerformedAction,
    routeParams,
    rows,
    scrollToTopOnFilter = true,
    scrollToTopOnSort = true,
    selectedRow,
    selectedRowIdDataPath,
    store,
    suppressPaginationPanel = true,
    watchLocationParams,
  } = props;

  const intl = useIntl();

  // PREV PROPS
  const prevPropsSelectedRow = usePrevious<SelectedRow | undefined>(
    selectedRow
  );
  const prevPropsWatchLocationParams = usePrevious<
    Record<string, any> | undefined
  >(watchLocationParams);
  const prevPropsCurrentPagination = usePrevious<
    Record<string, any> | undefined
  >(currentPagination);
  const prevPropsCurrentFilter = usePrevious<Record<string, any>[] | undefined>(
    currentFilter
  );
  const prevPropsCurrentScroll = usePrevious<number[] | undefined>(
    currentScroll
  );
  const prevPropsCurrentSort = usePrevious<CurrentSortModelItem[] | undefined>(
    currentSort
  );
  const prevPropsRows = usePrevious<RowsData | undefined>(rows);
  const prevPropsMemorizedColumnStates = usePrevious<
    MemorizedColumnStates | undefined
  >(memorizedColumnStates);

  // STATE
  const [gridColumns, setGridColumns] = useState<ColDef[]>([]);
  const [prevColumns, setPrevColumns] = useState<ColDef[]>([]);
  const [
    animationQueueEmptyAfterFirstRender,
    setAnimationQueueEmptyAfterFirstRender,
  ] = useState<boolean>(false);
  const [firstDataRendered, setFirstDataRendered] = useState<boolean>(false);
  const [firstResizeOccurred, setFirstResizeOccurred] =
    useState<boolean>(false);
  const [selectedRowState, setSelectedRowState] = useState<
    SelectedRow | undefined
  >(selectedRow);
  const [prevSelectedRow, setPrevSelectedRow] = useState<
    SelectedRow | undefined
  >(selectedRow ? {...selectedRow} : undefined);
  const [columnStates, setColumnStates] = useState<Record<string, any>>({});
  const [gridRows, setGridRows] = useState<RowsData>();
  const [prevRows, setPrevRows] = useState<RowsData>();
  const [pinnedBottomRows, setPinnedBottomRows] = useState<RowsData>();
  const [pinnedTopRows, setPinnedTopRows] = useState<RowsData>();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [prevIsTranslated, setPrevIsTranslated] = useState<boolean>(false);
  const [gridHeaderLevelsCount, setGridHeaderLevelsCount] = useState<number>(0);
  const [firstApplyColumnStates, setFirstApplyColumnStates] =
    useState<boolean>(false);

  // REFS
  const gridApi = useRef<GridApi | undefined>();
  const columnApi = useRef<ColumnApi | undefined>();
  const cancelablePromises = useRef<CancelablePromise<any>[]>([]);
  const ro = useRef<ResizeObserver | undefined>();
  const _isMounted = useRef<boolean>(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const popupParent = useRef<HTMLBodyElement | null>(
    document.querySelector('body')
  );

  const ROW_HEIGHT = gridOptions?.rowHeight ? gridOptions.rowHeight : 28;
  const ROW_PADDINGS = 5;
  const GRID_PADDINGS = 2;
  const TOTAL_ROW_HEIGHT = ROW_HEIGHT + ROW_PADDINGS;

  // on mount
  useEffect(() => {
    // _isMounted flag because ag-grid component still calls its events (handleFirstDataRender,
    // handleGridReady, ...) after unmounting
    _isMounted.current = true; // mounted
    return () => {
      if (ro.current) {
        ro.current.disconnect();
      }

      forEach(cancelablePromises.current, (cancelablePromise) => {
        cancelablePromise.cancel();
      });

      _isMounted.current = false; // unmounted
    };
  }, []);

  const changeRouteLoading = useCallback(
    (up = false, reset = false) => {
      if (onChangeRouteLoading) {
        onChangeRouteLoading(id, context, up, reset);
      }
    },
    [id, context, onChangeRouteLoading]
  );

  const updatePerformedAction = useCallback(
    (performedActionType, remove = true, reset = false) => {
      if (onUpdatePerformedAction) {
        onUpdatePerformedAction(
          id,
          context,
          performedActionType,
          remove,
          reset
        );
      }
    },
    [onUpdatePerformedAction, id, context]
  );

  const sizeColumnsToFit = useCallback(
    (skipCheck = false) => {
      if (gridApi.current && !isEmpty(tableRef.current)) {
        const bodyViewportElem = tableRef.current.querySelector(
          GRID_SCROLLABLE_ELEMENT_SELECTOR
        );
        if (
          skipCheck ||
          (isEmpty(memorizedColumnStates) &&
            (!bodyViewportElem || bodyViewportElem.clientWidth > 0))
        ) {
          // size columns only if body viewport has non-zero width
          callApiInTimeoutCallback('sizeColumnsToFit');
        }
      }
    },
    [memorizedColumnStates]
  );

  const handleResize = useCallback(() => {
    if (!firstResizeOccurred) {
      setFirstResizeOccurred(true);
    }
    sizeColumnsToFit(firstResizeOccurred);
    if (ColumnConfig.hasHeightByContentColumn(gridColumns)) {
      callApiInTimeoutCallback('resetRowHeights');
    }
  }, [firstResizeOccurred, gridColumns, sizeColumnsToFit]);

  const debouncedHandleResize = useMemo(
    () => debounce(handleResize, 500).bind(this),
    [handleResize]
  );

  // constructor && componentDidMount
  useEffect(() => {
    if (!isInitialized) {
      setStateFromRows(rows, gridOptions);

      if (!isEmpty(tableRef.current)) {
        if (ResizeObserver) {
          ro.current = new ResizeObserver(debouncedHandleResize);
          ro.current.observe(tableRef.current);
        }
      }
      updatePerformedAction(GRID_ACTION_FIRST_RENDER, false);

      changeRouteLoading(true);
      setIsInitialized(true);
    }
  }, [
    changeRouteLoading,
    isInitialized,
    rows,
    gridOptions,
    updatePerformedAction,
    debouncedHandleResize,
  ]);

  const setStateFromRows = (rows: RowsData | undefined, gridOptions) => {
    const stateFromRows = getStateFromRows(rows, gridOptions);
    if (stateFromRows.gridRows) {
      setGridRows(stateFromRows.gridRows);
    }
    if (stateFromRows.pinnedBottomRows) {
      setPinnedBottomRows(stateFromRows.pinnedBottomRows);
    }
    if (stateFromRows.pinnedTopRows) {
      setPinnedTopRows(stateFromRows.pinnedTopRows);
    }
    if (stateFromRows.hiddenColumns) {
      setHiddenColumns(stateFromRows.hiddenColumns);
    }
  };

  // getDerivedStateFromProps
  useEffect(() => {
    if (!isEqual(selectedRow, prevSelectedRow)) {
      setSelectedRowState(selectedRow);
      setPrevSelectedRow(selectedRowState);
    }
    if (!isEqual(rows, prevRows)) {
      let newPrevRows = rows;
      if (rows) {
        newPrevRows = isArray(rows) ? [...rows] : {...rows};
      }
      setStateFromRows(rows, gridOptions);
      setPrevRows(newPrevRows);
    }
    if (!isEqual(columns, prevColumns) || isTranslated !== prevIsTranslated) {
      setPrevColumns([...columns]);
      setPrevIsTranslated(isTranslated);
      setGridColumns(
        getStateGridColumns(columns, isTranslated, styles.dlAgCellWrapText, {
          navigate,
          intl,
          store,
          routeParams,
        })
      );
      setGridHeaderLevelsCount(getGridHeaderLevelsCount(columns));
    }
  }, [
    selectedRow,
    prevSelectedRow,
    selectedRowState,
    rows,
    prevRows,
    columns,
    prevColumns,
    isTranslated,
    prevIsTranslated,
    gridOptions,
    navigate,
    intl,
    store,
    routeParams,
  ]);

  /**
   * Scroll cache is disabled only when autoPageSize is enabled and currentPagination is set (i.e. not null)
   * @returns {boolean}
   */
  const isScrollCacheEnabled = useCallback(() => {
    // equivalent to !currentPagination || !autoPageSize
    return !(currentPagination && autoPageSize);
  }, [autoPageSize, currentPagination]);

  const setColumnVisibility = useCallback(
    (columns: ColDef[], hiddenColumns: string[]) => {
      if (columnApi.current) {
        const visibleCols: string[] = [];
        columns.forEach((column) => {
          if (column.field && !hiddenColumns.includes(column.field)) {
            visibleCols.push(column.field);
          }
        });
        columnApi.current.setColumnsVisible(visibleCols, true);
        columnApi.current.setColumnsVisible(hiddenColumns, false);
        sizeColumnsToFit();
      }
    },
    [sizeColumnsToFit]
  );

  const setScrollPosition = useCallback((scrollPosition) => {
    if (!isEmpty(scrollPosition) && scrollPosition.length > 0) {
      const cancelablePromise = utils.PromiseUtils.makeCancelable(
        callApiInTimeoutCallback(
          'ensureIndexVisible',
          scrollPosition[0],
          gridApi.current,
          600,
          'top'
        )
      );
      cancelablePromises.current.push(cancelablePromise);
      cancelablePromise.promise
        .then(() => {
          if (
            tableRef.current &&
            !isNil(scrollPosition[1]) &&
            scrollPosition[1] > 0
          ) {
            const scrollableDiv = tableRef.current.querySelector(
              GRID_SCROLLABLE_ELEMENT_SELECTOR
            );
            if (scrollableDiv) {
              scrollableDiv.scrollTop += scrollPosition[1];
            }
          }
        })
        .catch(utils.PromiseUtils.handleCanceledPromise);
    }
  }, []);

  const setPagination = useCallback((currentPagination) => {
    callApiInTimeoutCallback(
      'paginationSetPageSize',
      currentPagination.pageSize
    );
    callApiInTimeoutCallback('paginationGoToPage', currentPagination.page - 1);
  }, []);

  const setFilterModel = useCallback((currentFilter) => {
    const filterModel = {};
    forEach(currentFilter, (item) => {
      filterModel[item.property] = item;
    });
    callApiInTimeoutCallback('setFilterModel', filterModel);
  }, []);

  const setSortModel = useCallback(
    (sortModel: SortModelItem[], columnApi?: GridApi | ColumnApi) => {
      callApiInTimeoutCallback(
        'applyColumnState',
        mapSortModelToColumnStateUpdate(sortModel),
        columnApi
      );
    },
    []
  );

  /**
   * Sets AgGrid domLayout according to value of gridOptions.autoHeightLimit. But only if gridOptions.autoHeightLimit is set.
   */
  const updateDomLayout = useCallback(() => {
    if (gridOptions?.autoHeightLimit) {
      const ownRows = isArray(rows) || !rows?.data ? rows : rows.data;
      if (gridOptions.autoHeightLimit <= ownRows?.length) {
        callApiInTimeoutCallback('setDomLayout', 'normal');
      } else {
        callApiInTimeoutCallback('setDomLayout', 'autoHeight');
      }
    }
  }, [gridOptions?.autoHeightLimit, rows]);

  // useEffect(() => {
  //   if (gridOptions?.grid?.domLayout) {
  //     callApiInTimeoutCallback('setDomLayout', gridOptions.grid.domLayout);
  //     console.log('gridOptions.grid.domLayout', gridOptions.grid.domLayout);
  //   }
  // }, [gridOptions?.grid]);

  // componentDidUpdate
  useEffect(() => {
    // TODO: do it only if necessary - TEMPORARY DISABLED because it somehow fires scroll to top event
    // callApiInTimeoutCallback('resetRowHeights');

    if (
      gridApi.current &&
      selectedRow &&
      !isEqual(selectedRow, prevSelectedRow)
    ) {
      callApiInTimeoutCallback('redrawRows');
    }

    if (
      gridApi.current &&
      !isEqual(watchLocationParams, prevPropsWatchLocationParams)
    ) {
      // watched location params configuration has changed, it is necessary to refresh cells
      // this is solution for refreshing link renderers and their searchParams
      // here is some room for optimization in future - refresh only columns/cells with link renderers
      callApiInTimeoutCallback('refreshCells', {force: true});
    }

    if (
      gridApi.current &&
      currentPagination &&
      !isEqual(currentPagination, prevPropsCurrentPagination)
    ) {
      setPagination(currentPagination);
      updatePerformedAction(GRID_ACTION_PAGE);
      changeRouteLoading();
    }

    if (
      gridApi.current &&
      currentFilter &&
      !isEqual(currentFilter, prevPropsCurrentFilter)
    ) {
      setFilterModel(currentFilter);
      if (scrollToTopOnFilter && isScrollCacheEnabled()) {
        // reset scroll position
        setScrollPosition(STATIC_EMPTY_SCROLL);
      }
      updatePerformedAction(GRID_ACTION_FILTER);
      changeRouteLoading();
    }

    if (gridApi.current) {
      // enhance
      if (!isEqual(currentSort, prevPropsCurrentSort) && currentSort) {
        setSortModelAndScroll(
          setSortModel,
          currentSort,
          gridOptions,
          scrollToTopOnSort,
          isScrollCacheEnabled,
          setScrollPosition,
          columnApi,
          updatePerformedAction,
          changeRouteLoading
        );
      } else if (!isEmpty(gridOptions?.sortModel)) {
        getGridColumnStatePromise(
          columnApi.current,
          cancelablePromises,
          callApiInTimeoutCallback
        )
          .then((sortModel) => {
            if (isEmpty(sortModel)) {
              setSortModelAndScroll(
                setSortModel,
                currentSort,
                gridOptions,
                scrollToTopOnSort,
                isScrollCacheEnabled,
                setScrollPosition,
                columnApi,
                updatePerformedAction,
                changeRouteLoading
              );
            }
          })
          .catch(utils.PromiseUtils.handleCanceledPromise);
      }
    }

    if (
      gridApi.current &&
      currentScroll &&
      !isEqual(currentScroll, prevPropsCurrentScroll)
    ) {
      updatePerformedAction(GRID_ACTION_SCROLL);
    }

    if (
      columnApi.current &&
      !isEqual(rows, prevPropsRows) &&
      gridOptions?.hideEmptyColumns
    ) {
      setColumnVisibility(columns, hiddenColumns);
    }
    if (
      columnApi.current &&
      !isEqual(rows, prevPropsRows) &&
      gridOptions?.autoHeightLimit
    ) {
      // autoHeight in domLayout or autoHeightLimit is on
      updateDomLayout();
    }

    if (
      columnApi.current &&
      memorizedColumnStates &&
      !isEqual(memorizedColumnStates, prevPropsMemorizedColumnStates)
    ) {
      callApiInTimeoutCallback(
        'applyColumnState',
        {
          state: map(memorizedColumnStates, (value, key) => ({
            colId: key,
            ...value,
          })),
        },
        columnApi.current
      );
      if (
        hasColumnVisibilityChanged(
          memorizedColumnStates,
          prevPropsMemorizedColumnStates
        )
      ) {
        // call sizeColumnsToFit only when hide prop of some column(s) has changed
        sizeColumnsToFit(true);
      }
    }
  }, [
    changeRouteLoading,
    columns,
    currentFilter,
    currentPagination,
    currentScroll,
    currentSort,
    gridOptions,
    gridOptions?.autoHeightLimit,
    gridOptions?.hideEmptyColumns,
    hiddenColumns,
    isScrollCacheEnabled,
    memorizedColumnStates,
    prevPropsCurrentFilter,
    prevPropsCurrentPagination,
    prevPropsCurrentScroll,
    prevPropsCurrentSort,
    prevPropsMemorizedColumnStates,
    prevPropsRows,
    prevPropsSelectedRow,
    prevPropsWatchLocationParams,
    rows,
    scrollToTopOnFilter,
    scrollToTopOnSort,
    selectedRow,
    selectedRowState,
    setColumnVisibility,
    setFilterModel,
    setPagination,
    setScrollPosition,
    setSortModel,
    sizeColumnsToFit,
    updateDomLayout,
    updatePerformedAction,
    watchLocationParams,
  ]);

  // componentWillUnmount TODO: check if working correctly
  useEffect(() => {
    return () => {
      // reset component state in redux store
      updatePerformedAction(GRID_ACTION_FIRST_RENDER, true, true);
      changeRouteLoading(false, true);
    };
  }, [updatePerformedAction, changeRouteLoading]);

  const handleGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      columnApi.current = params.columnApi;

      if (!_isMounted.current) {
        return;
      }

      if (!isLoading) {
        setSortModel(
          applyDefaultSortModel(currentSort, gridOptions?.sortModel),
          columnApi.current
        );
      }

      if (currentFilter && !isLoading) {
        setFilterModel(currentFilter);
      }

      if (columnApi.current && gridOptions?.hideEmptyColumns && !isLoading) {
        setColumnVisibility(columns, hiddenColumns);
      }

      if (columnApi.current && gridOptions?.autoHeightLimit && !isLoading) {
        // autoHeight in domLayout or autoHeightLimit is on
        updateDomLayout();
      }

      if (columnApi.current && !isEmpty(memorizedColumnStates)) {
        callApiInTimeoutCallback(
          'applyColumnState',
          {
            state: map(memorizedColumnStates, (value, key) => ({
              colId: key,
              ...value,
            })),
          },
          columnApi.current
        );
      }

      // event onFirstDataRendered is not raised when there is no rows, or data is null. Remove performed actions
      if (!gridRows || gridRows.length === 0) {
        const cancelablePromise = utils.PromiseUtils.makeCancelable(
          Promise.resolve()
        );
        cancelablePromises.current.push(cancelablePromise);
        cancelablePromise.promise
          .then(() => {
            setFirstDataRendered(true);
          })
          .catch(utils.PromiseUtils.handleCanceledPromise)
          .finally(() => {
            // set down route loading twice
            // - once for a standard handleGridReady situation
            // - once for a onFirstDataRender situation, which is not called when there are no data
            changeRouteLoading();
            changeRouteLoading();
            updatePerformedAction(GRID_ACTION_FIRST_RENDER);
          });
      } else {
        changeRouteLoading();
      }
    },
    [
      changeRouteLoading,
      columns,
      currentFilter,
      currentSort,
      gridOptions.autoHeightLimit,
      gridOptions?.hideEmptyColumns,
      gridOptions?.sortModel,
      gridRows,
      hiddenColumns,
      isLoading,
      memorizedColumnStates,
      setColumnVisibility,
      setFilterModel,
      setSortModel,
      updatePerformedAction,
      updateDomLayout,
    ]
  );

  const handleFirstDataRendered = useCallback(() => {
    if (!_isMounted.current) {
      return;
    }

    if (columnApi.current && memorizedColumnStates && !firstApplyColumnStates) {
      callApiInTimeoutCallback(
        'applyColumnState',
        {
          state: map(memorizedColumnStates, (value, key) => ({
            colId: key,
            ...value,
          })),
        },
        columnApi.current
      );
      setFirstApplyColumnStates(true);
    }

    sizeColumnsToFit();

    const cancelablePromise = utils.PromiseUtils.makeCancelable(
      Promise.resolve()
    );
    cancelablePromises.current.push(cancelablePromise);
    cancelablePromise.promise
      .then(() => {
        // this runs only on mounted component
        setFirstDataRendered(true);

        if (
          gridApi.current &&
          currentScroll &&
          !isLoading &&
          isScrollCacheEnabled()
        ) {
          setScrollPosition(currentScroll);
        }

        if (gridApi.current && currentPagination && !isLoading) {
          setPagination(currentPagination);
        }
      })
      .catch(utils.PromiseUtils.handleCanceledPromise)
      .finally(() => {
        changeRouteLoading();
        updatePerformedAction(GRID_ACTION_FIRST_RENDER);
      });
  }, [
    changeRouteLoading,
    currentPagination,
    currentScroll,
    isLoading,
    isScrollCacheEnabled,
    setPagination,
    setScrollPosition,
    sizeColumnsToFit,
    updatePerformedAction,
  ]);

  const handleAnimationQueueEmpty = useCallback(() => {
    if (!_isMounted.current) {
      return;
    }

    // JM NOTE: here was prevState.firstDataRendered
    if (firstDataRendered) {
      setAnimationQueueEmptyAfterFirstRender(true);
    }
  }, [firstDataRendered]);

  const getColumnStatesPromise = useCallback(
    (columnApi) => {
      const cancelablePromise = utils.PromiseUtils.makeCancelable(
        callApiInTimeoutCallback('getColumnState', undefined, columnApi)
      );
      cancelablePromises.current.push(cancelablePromise);
      return cancelablePromise.promise
        .then((columnStatesFromGridApi) => {
          return getColumnStates(columnStates, columnStatesFromGridApi);
        })
        .catch(utils.PromiseUtils.handleCanceledPromise);
    },
    [columnStates]
  );

  const setColumnStatesHandler = useCallback(
    (columnApi) => {
      getColumnStatesPromise(columnApi).then((newColumnStates) => {
        if (newColumnStates) {
          setColumnStates(newColumnStates);
        }
      });
    },
    [getColumnStatesPromise]
  );

  const handleColumnResized = useCallback(
    (params) => {
      if (!_isMounted.current) {
        return;
      }

      getColumnStatesPromise(params.columnApi).then((columnStates) => {
        if (columnStates) {
          if (onColumnStatesChange) {
            onColumnStatesChange(columnStates);
          }
        }
      });

      //  performance improvement
      //  used only for columns with heightByContent = true
      //  FIX #2837 - do not check for grouped columns
      if (
        params.column == null ||
        ColumnConfig.checkColumnHasHeightByContent(columns, params.column.colId)
      ) {
        setColumnStatesHandler(params.columnApi);
        callApiInTimeoutCallback('resetRowHeights');
      }
    },
    [
      columns,
      getColumnStatesPromise,
      onColumnStatesChange,
      setColumnStatesHandler,
    ]
  );

  const debouncedHandleColumnResized = useMemo(
    () => debounce(handleColumnResized, 500).bind(this),
    [handleColumnResized]
  );

  const handleColumnGroupOpened = useCallback(() => {
    if (!_isMounted.current) {
      return;
    }

    sizeColumnsToFit(true);
  }, [sizeColumnsToFit]);

  const handleFilterButtonClick = useCallback(
    (button) => {
      if (gridApi.current) {
        const cancelablePromise = utils.PromiseUtils.makeCancelable(
          callApiInTimeoutCallback('getFilterInstance', button.getColumnId())
        );
        cancelablePromises.current.push(cancelablePromise);
        cancelablePromise.promise
          .then((filterComponent) => {
            if (filterComponent) {
              filterComponent.setModel(button.getCondition());
            } else {
              logger.warn(
                `Unknown ${button.getColumnId()}, check filter buttons configuration`
              );
            }

            const anotherCancelablePromise = utils.PromiseUtils.makeCancelable(
              callApiInTimeoutCallback('onFilterChanged')
            );
            cancelablePromises.current.push(anotherCancelablePromise);
            anotherCancelablePromise.promise
              .then(() => {
                if (onFilterButtonClick) {
                  onFilterButtonClick(button.getId());
                }
              })
              .catch(utils.PromiseUtils.handleCanceledPromise);
          })
          .catch(utils.PromiseUtils.handleCanceledPromise);
      }
    },
    [onFilterButtonClick]
  );

  const handleBodyScroll = useCallback(() => {
    if (!_isMounted.current) {
      return;
    }

    if (onScrollChange && firstDataRendered && isScrollCacheEnabled()) {
      // store scrollPosition
      const cancelablePromise = utils.PromiseUtils.makeCancelable(
        callApiInTimeoutCallback('getFirstDisplayedRow')
      );
      cancelablePromises.current.push(cancelablePromise);
      cancelablePromise.promise
        .then((firstDisplayedRowIndex) => {
          if (isNil(firstDisplayedRowIndex)) {
            logger.warn(
              'gridApi.getFirstDisplayedRow returned null or undefined' +
                ', scroll position was not stored!'
            );
          } else {
            const cancelablePromise3 = utils.PromiseUtils.makeCancelable(
              callApiInTimeoutCallback(
                'getDisplayedRowAtIndex',
                firstDisplayedRowIndex
              )
            );
            cancelablePromises.current.push(cancelablePromise3);
            cancelablePromise3.promise
              .then((firstDisplayedRowNode) => {
                if (isNil(firstDisplayedRowNode)) {
                  logger.warn(
                    'gridApi.getDisplayedRowAtIndex returned null or undefined' +
                      ', scroll position was not stored!'
                  );
                } else {
                  const scrollableDiv = tableRef.current?.querySelector(
                    GRID_SCROLLABLE_ELEMENT_SELECTOR
                  );
                  if (scrollableDiv) {
                    const rowNodeScrollOffset =
                      scrollableDiv.scrollTop - firstDisplayedRowNode.rowTop;
                    onScrollChange([
                      firstDisplayedRowIndex,
                      rowNodeScrollOffset,
                    ]);
                  }
                }
              })
              .catch(utils.PromiseUtils.handleCanceledPromise);
          }
        })
        .catch(utils.PromiseUtils.handleCanceledPromise);
    }
  }, [firstDataRendered, isScrollCacheEnabled, onScrollChange]);

  const debouncedHandleBodyScroll = useMemo(
    () => debounce(handleBodyScroll, 500).bind(this),
    [handleBodyScroll]
  );

  const handleGridSort = useCallback(
    (gridModel) => {
      if (!_isMounted.current) {
        return;
      }

      if (gridOptions && gridOptions[GRID_OPTIONS_REDRAW_ON_SORT]) {
        // some renderers need to be re-rendered on sorting
        gridModel.api.redrawRows();
      }
      if (onSortChange) {
        getGridColumnStatePromise(
          gridModel.columnApi,
          cancelablePromises,
          callApiInTimeoutCallback
        )
          .then((sortModel) => {
            // server sorting
            onSortChange(
              removeDefaultSortModel(sortModel, gridOptions?.sortModel)
            );
          })
          .catch(utils.PromiseUtils.handleCanceledPromise);
      }
    },
    [gridOptions, onSortChange]
  );

  const handleGridFilter = useCallback(
    (gridModel) => {
      if (!_isMounted.current) {
        return;
      }

      if (onFilterChange) {
        const cancelablePromise = utils.PromiseUtils.makeCancelable(
          callApiInTimeoutCallback('getFilterModel', undefined, gridModel.api)
        );
        cancelablePromises.current.push(cancelablePromise);
        cancelablePromise.promise
          .then((newFilterModel) => {
            const filterModel = map(newFilterModel, (item, key) => {
              item.property = item.property || key;
              return item;
            });
            // server filtering
            onFilterChange(filterModel);
          })
          .catch(utils.PromiseUtils.handleCanceledPromise);
      }
    },
    [onFilterChange]
  );

  const handleGridPagination = useCallback(
    (eventArgs) => {
      if (!_isMounted.current) {
        return;
      }

      if (gridApi.current && onPaginationChange && firstDataRendered) {
        const cancelablePromise = utils.PromiseUtils.makeCancelable(
          Promise.all([
            callApiInTimeoutCallback('paginationGetCurrentPage'),
            callApiInTimeoutCallback('paginationGetTotalPages'),
            callApiInTimeoutCallback('paginationGetPageSize'),
          ])
        );
        const isPageChange = eventArgs.newPage;
        cancelablePromises.current.push(cancelablePromise);
        cancelablePromise.promise
          .then(([page, pages, pageSize]) => {
            const newPagination: Pagination = {
              page:
                isPageChange || !currentPagination
                  ? page + 1
                  : currentPagination.page,
              pages,
              ...(pageSize > 0 ? {pageSize} : {}),
            };
            if (!isEqual(newPagination, currentPagination)) {
              // call handler only if pagination was changed internally by component
              // (e.g. due a resize) and not by props change
              onPaginationChange(newPagination);
            }
          })
          .catch(utils.PromiseUtils.handleCanceledPromise);
      }
    },
    [currentPagination, firstDataRendered, onPaginationChange]
  );

  const handleModelUpdated = useCallback(
    (gridModel) => {
      if (!_isMounted.current) {
        return;
      }

      if (gridModel.api && firstDataRendered) {
        if (gridModel.api.rowModel && gridModel.api.rowModel.rowsToDisplay) {
          // handle displaying noRowsOverlay when shown rows has updated
          const shownRows = gridModel.api.rowModel.rowsToDisplay;
          if (shownRows.length === 0) {
            gridModel.api.showNoRowsOverlay();
          } else {
            gridModel.api.hideOverlay();
          }
        }
      }
    },
    [firstDataRendered]
  );

  const getDefaultRowHeight = useCallback(
    (subRows = 1) => {
      return (ROW_HEIGHT + ROW_PADDINGS) * subRows;
    },
    [ROW_HEIGHT]
  );

  const getRowHeight = useCallback(
    (params) => {
      let subRows = 1;
      if (params && params.data && columnStates) {
        if (params.data[TABLE_ROW_MAX_SUB_ROWS]) {
          subRows = params.data[TABLE_ROW_MAX_SUB_ROWS];
        } else if (ColumnConfig.hasHeightByContentColumn(gridColumns)) {
          subRows = ColumnConfig.computeRowHeightByContent(
            params,
            gridColumns,
            columnStates,
            intl
          );
        }
      }
      return getDefaultRowHeight(subRows);
    },
    [columnStates, getDefaultRowHeight, gridColumns, intl]
  );

  const getRowStyle = useCallback(
    (params) => {
      if (params.node.rowPinned) {
        return {...gridOptions?.pinnedRowStyle};
      }
      return undefined;
    },
    [gridOptions?.pinnedRowStyle]
  );

  const getRowClass = useCallback(
    (params) => {
      if (
        selectedRowState &&
        selectedRowIdDataPath &&
        params.node.data &&
        selectedRowState.id &&
        get(params.node.data, selectedRowIdDataPath) &&
        selectedRowState.id === get(params.node.data, selectedRowIdDataPath)
      ) {
        return 'dlAgGridPreSelectedRow';
      }
      return undefined;
    },
    [selectedRowIdDataPath, selectedRowState]
  );

  /**
   * All gridApi methods are called in a timeout callback because sometimes
   * the underlying structures are not well-initialized in time of a method call.
   * It handles issues like the React invariant #170:
   * "Expected subtree parent to be a mounted class component."
   * @param apiMethodName Name of gridApi method.
   * @param param1 Optional parameter for gridApi method.
   * @param apiParam GridApi od ColumnApi can be optionally passed by argument.
   * @param timeoutParam timeout in milliseconds.
   * @param param2 Optional second parameter for gridApi method.
   */
  const callApiInTimeoutCallback = (
    apiMethodName: string,
    param1?: any,
    apiParam?: GridApi | ColumnApi,
    timeoutParam?: number,
    param2?: any
  ) => {
    const api = apiParam || gridApi.current;

    return callGridApiInTimeoutCallback(
      apiMethodName,
      api,
      tableRef.current,
      cancelablePromises.current,
      param1,
      timeoutParam,
      param2
    );
  };

  const localeTextFunc = useCallback(
    (id, defaultMessage) => {
      return utils.TranslateUtils.translate(
        {
          id: `${GRID_LOCALIZATION_PREFIX}${id}`,
          defaultMessage,
        },
        intl
      );
    },
    [intl]
  );

  /**
   * Logs debug message to console with information about (calling) method and instance id.
   * @param {Function|String} fn Method which name will be in the start of logged message.
   * @param {String} message  Additional logged message.
   * @example
   * // call this method with function in first argument
   * _logDebugMessageByInstance(handleFirstDataRendered);
   * // call this method with function name in first argument
   * _logDebugMessageByInstance('handleFirstDataRendered Promise');
   */
  // eslint-disable-next-line react/no-unused-class-component-methods
  const _logDebugMessageByInstance = (fn, message = '') => {
    const fnName = fn && fn.name ? fn.name : fn;
    const contextInfo = context ? `(${JSON.stringify(context)})` : '';
    console.log(`${fnName}(${id})${contextInfo} ${message}`);
  };

  const computeFixedHeight = () => {
    return (
      ((gridOptions?.autoHeightLimit ?? 0) + 1) * TOTAL_ROW_HEIGHT +
      GRID_PADDINGS
    );
  };

  // COMPUTED VALUES
  const dynamicProps = {
    ...(ColumnConfig.hasAutoHeightColumn(gridColumns)
      ? {rowHeight: getDefaultRowHeight()}
      : {getRowHeight}),
  };
  const paginationPageSize = currentPagination
    ? currentPagination.pageSize
    : undefined;

  const inAutoHeightLimit = useMemo(() => {
    // autoHeight in domLayout or autoHeightLimit is on
    return !!(
      gridOptions?.autoHeightLimit &&
      gridRows &&
      gridOptions.autoHeightLimit > gridRows.length
    );
  }, [gridOptions.autoHeightLimit, gridRows]);
  const isAutoHeight =
    TableConfig.isAutoHeightDomLayout(gridOptions) ||
    domLayout === 'autoHeight' ||
    inAutoHeightLimit;

  const gridParams = useMemo(
    () => ({...gridOptions?.grid}),
    [gridOptions?.grid]
  );
  const overlayNoRowsTemplate = useMemo(
    () =>
      getOverlayNoRowsTemplate(
        intl,
        I18N.NO_ROWS.id,
        gridApi.current,
        gridParams,
        gridHeaderLevelsCount,
        pinnedBottomRows,
        pinnedTopRows
      ),
    [gridHeaderLevelsCount, gridParams, intl, pinnedBottomRows, pinnedTopRows]
  );

  return (
    <div className={styles.dlAgGrid}>
      <AgGridFilterButtonItems
        filterButtonsConfig={filterButtonsConfig}
        handleItemClick={handleFilterButtonClick}
        currentFilterButton={currentFilterButton}
      />
      {/* outside of limit of autoheight, set limit height, otherwise set 100% */}
      <div
        style={{
          height:
            gridOptions?.autoHeightLimit && !inAutoHeightLimit
              ? `${computeFixedHeight()}px`
              : '100%',
        }}
      >
        <div className={styles.dlAgGridGridOuter}>
          <div
            ref={tableRef}
            className={classNames(className, styles.dlAgGridGridWrapper, {
              [styles.dlAgGridVerticalHeader]: gridOptions?.verticalHeader,
              [styles.dlAgGridTightLayout]: gridOptions?.tightLayout,
              [styles.dlAgGridNormalHeight]: isAutoHeight,
              [styles.dlAgGridFlexFixHeight]: !isAutoHeight,
            })}
            data-name={'ag-filetable-content'}
            data-state={
              animationQueueEmptyAfterFirstRender && !isPerformingAction
                ? GRID_STATE_STABILIZED
                : GRID_STATE_LOADING
            }
            data-context={JSON.stringify(context)}
          >
            <AgGridReact
              columnDefs={gridColumns}
              getRowStyle={getRowStyle}
              getRowClass={getRowClass}
              headerHeight={GRID_HEADER_HEIGHT}
              localeTextFunc={localeTextFunc}
              onBodyScroll={debouncedHandleBodyScroll}
              onAnimationQueueEmpty={handleAnimationQueueEmpty}
              onColumnGroupOpened={handleColumnGroupOpened}
              onComponentStateChanged={handleAnimationQueueEmpty}
              onModelUpdated={handleModelUpdated}
              onColumnResized={debouncedHandleColumnResized}
              onGridReady={handleGridReady}
              onFilterChanged={handleGridFilter}
              onFirstDataRendered={handleFirstDataRendered}
              onPaginationChanged={handleGridPagination}
              onSortChanged={handleGridSort}
              overlayNoRowsTemplate={overlayNoRowsTemplate}
              pagination={!!currentPagination}
              paginationAutoPageSize={autoPageSize}
              paginationPageSize={paginationPageSize}
              pinnedBottomRowData={transformRows(
                pinnedBottomRows,
                gridOptions,
                isDataTranslated,
                intl
              )}
              pinnedTopRowData={transformRows(
                pinnedTopRows,
                gridOptions,
                isDataTranslated,
                intl
              )}
              popupParent={popupParent.current}
              rowBuffer={0}
              rowData={transformRows(
                gridRows,
                gridOptions,
                isDataTranslated,
                intl
              )}
              suppressDragLeaveHidesColumns
              suppressPaginationPanel={suppressPaginationPanel}
              suppressPropertyNamesCheck
              suppressScrollOnNewData
              tooltipShowDelay={250}
              tooltipHideDelay={3000}
              domLayout={domLayout}
              {...dynamicProps}
              {...gridParams}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

AgGridComponent.displayName = 'AgGrid';

export const AgGrid = injectNavigate(injectStore(AgGridComponent));

const getStyles: GetStylesFunction = (options) => ({});
